import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  MdCheckCircleOutline,
  MdClose,
  MdDeleteOutline,
  MdEdit,
  MdHistory,
  MdNoteAdd,
  MdOutlinePushPin,
  MdPushPin,
} from 'react-icons/md';
import DashboardLayout from '../../components/DashboardLayout';
import stickyNoteService from '../../services/stickyNoteService';

const COLOR_SWATCHES = [
  '#fde68a',
  '#bbf7d0',
  '#bae6fd',
  '#fecdd3',
  '#ddd6fe',
  '#fee2e2',
];

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ComposerCard = styled.div`
  background: #ffffff;
  border-radius: 28px;
  padding: 32px;
  box-shadow: 0 25px 55px rgba(15, 23, 42, 0.18);
`;

const ComposerForm = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(280px, 2fr);
  gap: 32px;
  align-items: flex-start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  background: #ffffff;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
  flex-wrap: wrap;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  color: #0f172a;
`;

const HeaderSubtitle = styled.p`
  margin: 6px 0 0;
  color: #475569;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  width: 260px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px 14px;
  font-size: 15px;
  background: #f9fafb;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
    background: #ffffff;
  }
`;

const StatusFilterGroup = styled.div`
  display: inline-flex;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  padding: 4px;
`;

const StatusFilterButton = styled.button`
  border: none;
  background: ${(p) => (p.$active ? '#4f46e5' : 'transparent')};
  color: ${(p) => (p.$active ? '#ffffff' : '#374151')};
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 90;
`;

const ModalPanel = styled.div`
  width: min(960px, 95vw);
  position: relative;
`;

const ModalHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  color: #475569;
  font-size: 15px;
`;

const CloseModalButton = styled.button`
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f3f4f6;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  font-weight: 600;
`;

const TextInput = styled.input`
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 12px 14px;
  font-size: 15px;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 12px 14px;
  font-size: 15px;
  resize: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
  }
`;

const SwatchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const SwatchButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: ${(p) => (p.$selected ? '2px solid #111827' : '2px solid transparent')};
  background: ${(p) => p.$color};
  cursor: pointer;
  transition: transform 0.15s;
  &:hover {
    transform: translateY(-2px);
  }
`;

const ComposerActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ToggleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PillToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? '#4f46e5' : '#d1d5db')};
  background: ${(p) => (p.$active ? '#eef2ff' : '#ffffff')};
  color: ${(p) => (p.$active ? '#1f1d6a' : '#374151')};
  font-weight: 600;
  cursor: pointer;
  min-width: 150px;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  border-radius: 14px;
  border: none;
  background: ${(p) => (p.$disabled ? '#c7d2fe' : '#4f46e5')};
  color: #ffffff;
  font-weight: 600;
  font-size: 15px;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  box-shadow: 0 14px 30px rgba(79, 70, 229, 0.25);
`;

const ActionCluster = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const NotesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NoteCard = styled.article`
  position: relative;
  border-radius: 20px;
  padding: 20px;
  background: ${(p) => p.$color};
  min-height: 200px;
  box-shadow: 0 18px 30px rgba(15, 23, 42, 0.12);
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.$status === 'passed' ? '#92400e' : '#065f46')};
  background: ${(p) => (p.$status === 'passed' ? 'rgba(251, 191, 36, 0.35)' : 'rgba(16, 185, 129, 0.35)')};
  margin-top: 8px;
`;

const StatusToggle = styled.button`
  border: none;
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
`;

const NoteTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
`;

const NoteBody = styled.p`
  margin: 12px 0;
  font-size: 15px;
  line-height: 1.6;
  color: #111827;
  white-space: pre-wrap;
`;

const NoteMeta = styled.div`
  font-size: 12px;
  color: #4b5563;
`;

const CardActions = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  color: #1f2937;
  cursor: pointer;
  transition: transform 0.15s;
  &:hover {
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  padding: 60px;
  border-radius: 24px;
  border: 2px dashed #cbd5f5;
  text-align: center;
  color: #475569;
  font-size: 16px;
`;

const ErrorBanner = styled.div`
  width: 100%;
  border-radius: 16px;
  padding: 12px 16px;
  background: #fee2e2;
  color: #991b1b;
  font-weight: 500;
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 95;
`;

const ConfirmCard = styled.div`
  width: min(480px, 90vw);
  background: #ffffff;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 35px 65px rgba(15, 23, 42, 0.25);
  text-align: center;
`;

const ConfirmTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 20px;
  color: #0f172a;
`;

const ConfirmSubtitle = styled.p`
  margin: 0 0 24px;
  color: #475569;
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;

const SecondaryButton = styled.button`
  padding: 12px 20px;
  border-radius: 12px;
  border: 1px solid #d1d5db;
  background: #f8fafc;
  color: #1f2937;
  font-weight: 600;
  cursor: pointer;
  min-width: 120px;
`;

const DangerButton = styled.button`
  padding: 12px 20px;
  border-radius: 12px;
  border: none;
  background: #dc2626;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  min-width: 140px;
  box-shadow: 0 15px 30px rgba(220, 38, 38, 0.25);
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};
`;

const buildEmptyDraft = () => ({
  title: '',
  content: '',
  color: COLOR_SWATCHES[0],
  isPinned: false,
  status: 'active',
});

export default function StickyNote() {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState(() => buildEmptyDraft());
  const [isModalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingNoteId(null);
    setDraft(buildEmptyDraft());
  }, []);

  const upsertNote = useCallback((payload) => {
    if (!payload || payload.id === undefined || payload.id === null) return;
    setNotes((prev) => {
      const exists = prev.some((item) => item.id === payload.id);
      if (exists) {
        return prev.map((item) => (item.id === payload.id ? payload : item));
      }
      return [payload, ...prev];
    });
  }, []);

  const removeNote = useCallback((noteId) => {
    setNotes((prev) => prev.filter((note) => note.id !== noteId));
  }, []);

  const refreshNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetched = await stickyNoteService.fetchNotes();
      setNotes(Array.isArray(fetched) ? fetched : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load sticky notes', err);
      setError('Unable to load sticky notes. Please try again.');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshNotes();
  }, [refreshNotes]);

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isModalOpen, closeModal]);

  const sortedNotes = useMemo(() => {
    const toTime = (value) => {
      const date = new Date(value);
      const time = date.getTime();
      return Number.isNaN(time) ? 0 : time;
    };

    return [...notes].sort((a, b) => {
      if (a.isPinned !== b.isPinned) {
        return a.isPinned ? -1 : 1;
      }
      return toTime(b.updatedAt) - toTime(a.updatedAt);
    });
  }, [notes]);

  const searchTokens = useMemo(() => {
    return searchQuery
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
  }, [searchQuery]);

  const matchesSearch = useCallback((note) => {
    if (!searchTokens.length) return true;
    const haystack = `${note.title} ${note.content}`.toLowerCase();
    return searchTokens.every((token) => haystack.includes(token));
  }, [searchTokens]);

  const filteredNotes = useMemo(() => {
    return sortedNotes.filter((note) => {
      const statusMatches = statusFilter === 'all' ? true : note.status === statusFilter;
      return statusMatches && matchesSearch(note);
    });
  }, [sortedNotes, statusFilter, matchesSearch]);

  const handleDraftChange = (field) => (event) => {
    const value = event.target.value;
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleColorPick = (color) => {
    setDraft((prev) => ({ ...prev, color }));
  };

  const handleSubmitNote = async (event) => {
    event.preventDefault();
    const body = draft.content.trim();
    if (!body || saving) return;

    const payload = {
      title: draft.title.trim() || 'Untitled',
      content: body,
      color: draft.color,
      status: draft.status === 'passed' ? 'passed' : 'active',
      isPinned: Boolean(draft.isPinned),
    };

    setSaving(true);
    setError(null);

    try {
      let savedNote = null;
      if (editingNoteId) {
        savedNote = await stickyNoteService.updateNote(editingNoteId, payload);
      } else {
        savedNote = await stickyNoteService.createNote(payload);
      }

      if (savedNote) {
        upsertNote(savedNote);
      } else {
        await refreshNotes();
      }

      closeModal();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save sticky note', err);
      setError('Unable to save sticky note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePinToggle = async (note) => {
    if (!note) return;
    setError(null);
    try {
      const updated = await stickyNoteService.updatePin(note.id, !note.isPinned);
      if (updated) {
        upsertNote(updated);
      } else {
        await refreshNotes();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update pin state', err);
      setError('Unable to update pin state. Please try again.');
    }
  };

  const requestDeleteNote = (note) => {
    if (!note) return;
    setError(null);
    setDeleteTarget(note);
  };

  const cancelDeleteRequest = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const handleDeleteNote = async () => {
    if (!deleteTarget) return;
    setError(null);
    setDeleting(true);
    try {
      await stickyNoteService.deleteNote(deleteTarget.id);
      removeNote(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete sticky note', err);
      setError('Unable to delete sticky note. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusToggle = async (note) => {
    if (!note) return;
    const nextStatus = note.status === 'active' ? 'passed' : 'active';
    setError(null);
    try {
      const updated = await stickyNoteService.updateStatus(note.id, nextStatus);
      if (updated) {
        upsertNote(updated);
      } else {
        await refreshNotes();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update sticky note status', err);
      setError('Unable to update sticky note status. Please try again.');
    }
  };

  const formatTimestamp = (value) => {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const isSubmitDisabled = saving || !draft.content.trim();
  const openCreateModal = useCallback(() => {
    setEditingNoteId(null);
    setDraft(buildEmptyDraft());
    setError(null);
    setModalOpen(true);
  }, []);

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setError(null);
    setDraft({
      title: note.title || '',
      content: note.content || '',
      color: note.color || COLOR_SWATCHES[0],
      isPinned: Boolean(note.isPinned),
      status: note.status === 'passed' ? 'passed' : 'active',
    });
    setModalOpen(true);
  };

  const handleSearchChange = (event) => setSearchQuery(event.target.value);
  const handleStatusFilter = (value) => setStatusFilter(value);

  const hasSearch = searchTokens.length > 0;
  const hasNotes = notes.length > 0;

  return (
    <DashboardLayout title="Sticky Note">
      <PageWrap>
        <PageHeader>
          <div>
            <HeaderTitle>Sticky board</HeaderTitle>
            <HeaderSubtitle>Keep day-to-day reminders close and pinned for the team.</HeaderSubtitle>
          </div>
          <HeaderActions>
            <SearchInput
              type="search"
              placeholder="Search title or details"
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label="Search sticky notes"
            />
            <StatusFilterGroup role="group" aria-label="Filter stickies by status">
              {['active', 'passed', 'all'].map((option) => (
                <StatusFilterButton
                  key={option}
                  type="button"
                  $active={statusFilter === option}
                  onClick={() => handleStatusFilter(option)}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </StatusFilterButton>
              ))}
            </StatusFilterGroup>
            <PrimaryButton type="button" onClick={openCreateModal}>
              <MdNoteAdd /> Add new sticker
            </PrimaryButton>
          </HeaderActions>
        </PageHeader>

        {error && <ErrorBanner>{error}</ErrorBanner>}

        {loading ? (
          <EmptyState>Loading sticky notes...</EmptyState>
        ) : !hasNotes && !hasSearch ? (
          <EmptyState>
            Drop your first sticky on the board using "Add new sticker" to keep quick reminders close by.
          </EmptyState>
        ) : filteredNotes.length === 0 ? (
          <EmptyState>
            {hasSearch
              ? `No notes match "${searchQuery}". Try different keywords or clear the search box.`
              : statusFilter === 'all'
                ? 'No stickies to show yet. Add one or adjust the status filter.'
                : `No ${statusFilter} stickies right now. Switch the filter or add a new sticker.`}
          </EmptyState>
        ) : (
          <NotesGrid>
            {filteredNotes.map((note) => (
              <NoteCard key={note.id} $color={note.color}>
                <CardActions>
                  <IconButton type="button" onClick={() => handlePinToggle(note)} aria-label="Toggle pin">
                    {note.isPinned ? <MdPushPin /> : <MdOutlinePushPin />}
                  </IconButton>
                  <IconButton type="button" onClick={() => startEditNote(note)} aria-label="Edit note">
                    <MdEdit />
                  </IconButton>
                  <IconButton type="button" onClick={() => requestDeleteNote(note)} aria-label="Delete note">
                    <MdDeleteOutline />
                  </IconButton>
                </CardActions>
                <NoteTitle>{note.title}</NoteTitle>
                <StatusBadge $status={note.status}>
                  {note.status === 'passed' ? <MdHistory /> : <MdCheckCircleOutline />}
                  {note.status === 'passed' ? 'Passed' : 'Active'}
                </StatusBadge>
                <NoteBody>{note.content}</NoteBody>
                <NoteMeta>
                  Updated {formatTimestamp(note.updatedAt)}
                  <div>
                    <StatusToggle type="button" onClick={() => handleStatusToggle(note)}>
                      {note.status === 'active' ? 'Mark as passed' : 'Move to active'}
                    </StatusToggle>
                  </div>
                </NoteMeta>
              </NoteCard>
            ))}
          </NotesGrid>
        )}
      </PageWrap>
      {isModalOpen && (
        <ModalOverlay role="dialog" aria-modal="true" aria-labelledby="sticky-modal-title" onClick={closeModal}>
          <ModalPanel onClick={(event) => event.stopPropagation()}>
            <ComposerCard>
              <ModalHeading>
                <div>
                  <ModalTitle id="sticky-modal-title">{editingNoteId ? 'Update sticky note' : 'Add a new sticky note'}</ModalTitle>
                  <ModalSubtitle>
                    {editingNoteId
                      ? 'Make adjustments and keep the history intact for your team.'
                      : 'Notes sync with the admin backend, so everyone keeps the same context.'}
                  </ModalSubtitle>
                </div>
                <CloseModalButton type="button" onClick={closeModal} aria-label="Close modal">
                  <MdClose />
                </CloseModalButton>
              </ModalHeading>
              <ComposerForm onSubmit={handleSubmitNote}>
                <FieldStack>
                  <div>
                    <FieldLabel htmlFor="sticky-title">Title</FieldLabel>
                    <TextInput
                      id="sticky-title"
                      placeholder="Give this note a short name"
                      value={draft.title}
                      onChange={handleDraftChange('title')}
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="sticky-body">Details</FieldLabel>
                    <TextArea
                      id="sticky-body"
                      placeholder="Capture quick thoughts, handover notes, or action items..."
                      value={draft.content}
                      onChange={handleDraftChange('content')}
                    />
                  </div>
                </FieldStack>
                <FieldStack>
                  <div>
                    <FieldLabel>Color</FieldLabel>
                    <SwatchRow>
                      {COLOR_SWATCHES.map((swatch) => (
                        <SwatchButton
                          key={swatch}
                          type="button"
                          $color={swatch}
                          $selected={draft.color === swatch}
                          onClick={() => handleColorPick(swatch)}
                          aria-label={`Use ${swatch} as note color`}
                        />
                      ))}
                    </SwatchRow>
                  </div>
                  <ComposerActions>
                    <ActionCluster>
                      <FieldLabel as="span">Workflow</FieldLabel>
                      <ToggleRow>
                        <PillToggle type="button" $active={draft.isPinned} onClick={() => setDraft((prev) => ({ ...prev, isPinned: !prev.isPinned }))}>
                          {draft.isPinned ? <MdPushPin /> : <MdOutlinePushPin />}
                          {draft.isPinned ? 'Pinned note' : 'Pin on top'}
                        </PillToggle>
                        <PillToggle
                          type="button"
                          $active={draft.status === 'passed'}
                          onClick={() => setDraft((prev) => ({ ...prev, status: prev.status === 'passed' ? 'active' : 'passed' }))}
                        >
                          {draft.status === 'passed' ? <MdHistory /> : <MdCheckCircleOutline />}
                          {draft.status === 'passed' ? 'Marked as passed' : 'Keep active'}
                        </PillToggle>
                      </ToggleRow>
                    </ActionCluster>
                    <ActionFooter>
                      <PrimaryButton type="submit" $disabled={isSubmitDisabled} disabled={isSubmitDisabled} aria-busy={saving}>
                        {editingNoteId ? <MdEdit /> : <MdNoteAdd />}
                        {editingNoteId ? 'Update note' : 'Add note'}
                      </PrimaryButton>
                    </ActionFooter>
                  </ComposerActions>
                </FieldStack>
              </ComposerForm>
            </ComposerCard>
          </ModalPanel>
        </ModalOverlay>
      )}
      {deleteTarget && (
        <ConfirmOverlay role="dialog" aria-modal="true" aria-live="assertive" onClick={cancelDeleteRequest}>
          <ConfirmCard onClick={(event) => event.stopPropagation()}>
            <ConfirmTitle>Delete this note?</ConfirmTitle>
            <ConfirmSubtitle>
              "{deleteTarget.title || 'Untitled'}" will be permanently removed from the sticky board. This action cannot be undone.
            </ConfirmSubtitle>
            <ConfirmActions>
              <SecondaryButton type="button" onClick={cancelDeleteRequest} disabled={deleting}>
                Keep note
              </SecondaryButton>
              <DangerButton type="button" onClick={handleDeleteNote} $disabled={deleting} aria-busy={deleting}>
                {deleting ? 'Deleting…' : 'Delete note'}
              </DangerButton>
            </ConfirmActions>
          </ConfirmCard>
        </ConfirmOverlay>
      )}
    </DashboardLayout>
  );
}
